import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ArrowLeft } from 'lucide-react';

const getFirstImage = (product) => {
  if (!product || !product.images) return '';
  return product.images.length > 0 ? product.images[0] : '';
};

export default function CartPage() {
  const { cart, loading, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount } = useCart();
  
  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  const renderCartItem = (item) => {
    const product = item.product;
    const imgSrc = getFirstImage(product);
    const price = product?.sale_price || product?.price || 0;
    const itemTotal = price * item.quantity;

    return (
      <div key={item.product_id} className="grid grid-cols-1 sm:grid-cols-12 gap-4 py-6 border-b border-[#E5E7EB]" data-testid={`cart-item-${item.product_id}`}>
        <div className="sm:col-span-6 flex gap-4">
          <div className="w-24 h-24 bg-[#F9F7F2] flex-shrink-0">
            {imgSrc && <img src={imgSrc} alt={product?.name || ''} className="w-full h-full object-cover" />}
          </div>
          <div>
            <Link to={`/product/${item.product_id}`} className="font-medium text-[#1A1A1A] hover:text-[#8B5E3C]">
              {product?.name || 'Product'}
            </Link>
            <p className="text-sm text-[#6B7280] mt-1">{product?.style} • {product?.color}</p>
            <p className="text-sm text-[#6B7280]">SKU: {product?.sku}</p>
            <button onClick={() => removeFromCart(item.product_id)} disabled={loading} className="mt-2 text-sm text-red-500 flex items-center gap-1 sm:hidden">
              <Trash2 size={14} />Remove
            </button>
          </div>
        </div>

        <div className="sm:col-span-2 flex sm:justify-center items-start">
          <div className="quantity-selector">
            <button className="quantity-btn" onClick={() => updateQuantity(item.product_id, Math.max(1, item.quantity - 1))} disabled={loading}><Minus size={14} /></button>
            <span className="quantity-input">{item.quantity}</span>
            <button className="quantity-btn" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={loading}><Plus size={14} /></button>
          </div>
        </div>

        <div className="sm:col-span-2 sm:text-right">
          <span className="sm:hidden text-sm text-[#6B7280]">Price: </span>
          {product?.sale_price ? (
            <div className="inline sm:block">
              <span className="font-medium text-[#EF4444]">${product.sale_price.toFixed(2)}</span>
              <span className="text-sm text-[#9CA3AF] line-through ml-2 sm:ml-0 sm:block">${product.price.toFixed(2)}</span>
            </div>
          ) : (
            <span className="font-medium">${price.toFixed(2)}</span>
          )}
        </div>

        <div className="sm:col-span-2 flex justify-between sm:justify-end items-start gap-4">
          <div className="sm:text-right">
            <span className="sm:hidden text-sm text-[#6B7280]">Total: </span>
            <span className="font-semibold text-[#1A1A1A]">${itemTotal.toFixed(2)}</span>
          </div>
          <button onClick={() => removeFromCart(item.product_id)} disabled={loading} className="hidden sm:block text-[#9CA3AF] hover:text-red-500">
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="cart-page">
        <h1 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-8">Shopping Cart</h1>

        {isEmpty ? (
          <div className="text-center py-20" data-testid="empty-cart">
            <ShoppingBag size={64} className="mx-auto text-[#E5E7EB] mb-6" />
            <h2 className="font-manrope text-2xl font-semibold text-[#1A1A1A] mb-4">Your cart is empty</h2>
            <p className="text-[#6B7280] mb-8 max-w-md mx-auto">Start browsing to find your perfect kitchen cabinets.</p>
            <Button asChild size="lg"><Link to="/shop">Start Shopping<ArrowRight className="ml-2" size={18} /></Link></Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-6">
              <div className="hidden sm:grid grid-cols-12 gap-4 text-sm font-medium text-[#6B7280] pb-4 border-b border-[#E5E7EB]">
                <div className="col-span-6">Product</div>
                <div className="col-span-2 text-center">Quantity</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-right">Total</div>
              </div>

              {items.map(renderCartItem)}

              <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6">
                <Button variant="outline" asChild><Link to="/shop"><ArrowLeft className="mr-2" size={18} />Continue Shopping</Link></Button>
                <Button variant="outline" onClick={clearCart} disabled={loading} className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white" data-testid="clear-cart-btn">
                  <Trash2 className="mr-2" size={18} />Clear Cart
                </Button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-[#F9F7F2] p-6 sticky top-24" data-testid="order-summary">
                <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-6">Order Summary</h2>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[#6B7280]"><span>Subtotal ({cartCount} items)</span><span>${cartTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[#6B7280]"><span>Shipping</span><span>{cartTotal >= 2500 ? 'FREE' : 'Calculated at checkout'}</span></div>
                  {cartTotal < 2500 && (
                    <div className="bg-[#8B5E3C]/10 p-3 text-sm text-[#8B5E3C]">Add ${(2500 - cartTotal).toFixed(2)} more for FREE shipping</div>
                  )}
                </div>
                <div className="border-t border-[#E5E7EB] pt-4 mb-6">
                  <div className="flex justify-between text-lg font-semibold text-[#1A1A1A]"><span>Estimated Total</span><span>${cartTotal.toFixed(2)}</span></div>
                </div>
                <Button size="lg" className="w-full bg-[#1A1A1A] hover:bg-[#333333]" asChild>
                  <Link to="/checkout" data-testid="proceed-to-checkout-btn">Proceed to Checkout<ArrowRight className="ml-2" size={18} /></Link>
                </Button>
                <p className="text-xs text-[#6B7280] text-center mt-4">Taxes calculated at checkout</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
